import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getAllCooperators,
  createCooperator,
  updateCooperator,
  deleteCooperator,
  getAllRules, // <-- Import new Axios function
  getAllPermissions, // <-- Import new Axios function
} from "./CooperatorAxios"; // Assuming this is the correct path to your Axios functions

// Get All Cooperators
export const GetAllCooperators = createAsyncThunk(
  "cooperator/get-all",
  async (_, thunkAPI) => {
    try {
      return await getAllCooperators();
    } catch (error) {
      // Extracting a more useful error message if possible
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Create Cooperator
export const CreateCooperator = createAsyncThunk(
  "cooperator/create",
  async (data, thunkAPI) => {
    try {
      return await createCooperator(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          (error.response.data.message ||
            JSON.stringify(error.response.data.errors))) || // Handle validation errors
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update Cooperator
export const UpdateCooperator = createAsyncThunk(
  "cooperator/update",
  async (data, thunkAPI) => {
    try {
      return await updateCooperator(data); // data should be an object like { id, data }
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          (error.response.data.message ||
            JSON.stringify(error.response.data.errors))) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete Cooperator
export const DeleteCooperator = createAsyncThunk(
  "cooperator/delete",
  async (id, thunkAPI) => {
    try {
      return await deleteCooperator(id);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// --- NEW THUNKS for Rules and Permissions ---

// Get All Rules
export const GetAllRules = createAsyncThunk(
  "cooperator/get-all-rules",
  async (_, thunkAPI) => {
    try {
      return await getAllRules();
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get All Permissions
export const GetAllPermissions = createAsyncThunk(
  "cooperator/get-all-permissions",
  async (_, thunkAPI) => {
    try {
      return await getAllPermissions();
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Reset Action
export const resetStateCooperator = createAction("Reset_Cooperator_State");

// Initial State
const initialState = {
  cooperators: [],
  rules: [], // <-- New state variable for rules
  permissions: [], // <-- New state variable for permissions
  isLoading: false,
  isSuccess: false, // General success flag for CUD operations
  isAllSuccess: false, // Success flag specifically for fetching all cooperators
  rulesSuccess: false, // <-- Success flag for fetching rules
  permissionsSuccess: false, // <-- Success flag for fetching permissions
  isError: false,
  message: "",
  item: false, // Consider renaming or clarifying its purpose
  show: false, // Consider renaming or clarifying its purpose
  // You had 'updated' and 'deleted' flags implicitly by toggling them.
  // It's often clearer to have explicit flags or rely on 'isSuccess' and action type.
};

// Slice
export const cooperatorSlice = createSlice({
  name: "cooperator",
  initialState,
  reducers: {
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    // You might want a specific reducer to clear success flags after an operation if needed
    clearSuccessFlags: (state) => {
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Cooperators
      .addCase(GetAllCooperators.pending, (state) => {
        state.isLoading = true;
        state.isAllSuccess = false; // Reset before new fetch
      })
      .addCase(GetAllCooperators.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.isSuccess = true; // This flag is more for CUD, not usually for GET all
        state.isAllSuccess = true;
        state.isError = false;
        state.cooperators = action.payload?.data || action.payload; // Adjust if Axios returns data directly
        state.message = "";
      })
      .addCase(GetAllCooperators.rejected, (state, action) => {
        state.isLoading = false;
        // state.isSuccess = false;
        state.isAllSuccess = false;
        state.isError = true;
        state.message = action.payload || "Failed to load cooperators"; // Use payload from rejectWithValue
        state.cooperators = []; // Clear data on error
        toast.error(state.message);
      })

      // Create Cooperator
      .addCase(CreateCooperator.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false; // Reset before new operation
      })
      .addCase(CreateCooperator.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.isAllSuccess = false; // This relates to fetching all, not creating
        // Optionally add the new cooperator to the state.cooperators list
        // if (action.payload?.data) {
        //   state.cooperators.push(action.payload.data);
        // }
        state.message = "";
        toast.success(
          action.payload?.message || "Cooperator added successfully"
        );
      })
      .addCase(CreateCooperator.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        // state.isAllSuccess = true; // This logic seems incorrect for create reject
        state.message = action.payload || "Failed to create cooperator";
        toast.error(state.message);
      })

      // Update Cooperator
      .addCase(UpdateCooperator.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(UpdateCooperator.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.isAllSuccess = false;
        // state.updated = !state.updated; // This pattern can be tricky, consider specific flags or just isSuccess
        // Optionally update the cooperator in the state.cooperators list
        // const updatedCooperator = action.payload?.data;
        // if (updatedCooperator) {
        //   state.cooperators = state.cooperators.map(c => c.id === updatedCooperator.id ? updatedCooperator : c);
        // }
        state.message = "";
        toast.success(
          action.payload?.message || "Cooperator updated successfully"
        );
      })
      .addCase(UpdateCooperator.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message = action.payload || "Failed to update cooperator";
        toast.error(state.message);
      })

      // Delete Cooperator
      .addCase(DeleteCooperator.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(DeleteCooperator.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.isAllSuccess = false;
        // state.deleted = !state.deleted; // Similar to 'updated', consider alternatives
        // Optionally remove the cooperator from the state.cooperators list
        // const deletedId = action.meta.arg; // The ID passed to DeleteCooperator
        // state.cooperators = state.cooperators.filter(c => c.id !== deletedId);
        state.message = "";
        toast.success(
          action.payload?.message || "Cooperator deleted successfully"
        );
      })
      .addCase(DeleteCooperator.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message = action.payload || "Failed to delete cooperator";
        toast.error(state.message);
      })

      // --- Get All Rules ---
      .addCase(GetAllRules.pending, (state) => {
        state.isLoading = true;
        state.rulesSuccess = false; // Reset before new fetch
      })
      .addCase(GetAllRules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rulesSuccess = true;
        state.isError = false;
        state.rules = action.payload?.data || action.payload; // Adjust if Axios returns data directly
        state.message = "";
      })
      .addCase(GetAllRules.rejected, (state, action) => {
        state.isLoading = false;
        state.rulesSuccess = false;
        state.isError = true;
        state.message = action.payload || "Failed to load rules";
        state.rules = []; // Clear data on error
        toast.error(state.message);
      })

      // --- Get All Permissions ---
      .addCase(GetAllPermissions.pending, (state) => {
        state.isLoading = true;
        state.permissionsSuccess = false; // Reset before new fetch
      })
      .addCase(GetAllPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissionsSuccess = true;
        state.isError = false;
        state.permissions = action.payload?.data; // Adjust if Axios returns data directly
        state.message = "";
      })
      .addCase(GetAllPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.permissionsSuccess = false;
        state.isError = true;
        state.message = action.payload || "Failed to load permissions";
        state.permissions = []; // Clear data on error
        toast.error(state.message);
      })

      // Reset
      .addCase(resetStateCooperator, (state) => {
        // Return a copy of initialState to ensure all fields are reset
        // This is safer than manually resetting each field if initialState changes
        Object.assign(state, initialState);
      });
  },
});

export const { setItem, clearSuccessFlags } = cooperatorSlice.actions;
export default cooperatorSlice.reducer;
